/**
 * Apply a collection binding override to a source item list.
 *
 * Mode is per row: any id field (hiddenIds/orderedIds) makes the row id-based
 * and legacy index fields are ignored. The editor and migration script convert
 * hide + order atomically, so mixed rows are never written.
 *
 * Ordering contract: no orderedIds means live source order. When orderedIds is
 * present, known IDs render in the saved custom order and newly synced /
 * unlisted items append in current source order.
 */

import type { CollectionBindingOverride, CollectionCustomItem } from './types';

type ItemRecord = Record<string, unknown>;

export function collectionItemId(item: ItemRecord): string | null {
  const id = item.id;
  // Legacy rows store false/'false' for missing ids — never treat those as durable.
  if (typeof id !== 'string' || !id || id === 'false') return null;
  return id;
}

export function collectionCustomItemId(custom: CollectionCustomItem): string | null {
  if (typeof custom.id === 'string' && custom.id.length > 0) return custom.id;
  return collectionItemId(custom.item);
}

/**
 * Resolve a saved orderedIds list against the current items: known ids render
 * in the saved order, then unlisted (newly synced / id-less) items append in
 * current order.
 */
export function orderCollectionItemsByIds<T>(
  items: T[],
  orderedIds: string[],
  getId: (item: T) => string | null,
): T[] {
  const byId = new Map<string, T>();
  for (const item of items) {
    const id = getId(item);
    if (id && !byId.has(id)) byId.set(id, item);
  }

  const ordered: T[] = [];
  const seen = new Set<string>();
  for (const id of orderedIds) {
    const item = byId.get(id);
    if (!item || seen.has(id)) continue;
    ordered.push(item);
    seen.add(id);
  }
  for (const item of items) {
    const id = getId(item);
    if (id) {
      if (seen.has(id)) continue;
      seen.add(id);
    }
    ordered.push(item);
  }
  return ordered;
}

function applyLimit<T>(items: T[], limit: number | null | undefined): T[] {
  if (limit == null || limit < 0) return items;
  return items.slice(0, limit);
}

function injectCustomItemsByPosition<T extends ItemRecord>(
  items: T[],
  customItems: CollectionCustomItem[],
): T[] {
  const next = [...items];
  const sorted = [...customItems].sort((a, b) => a.position - b.position);
  for (const custom of sorted) {
    const entry = (custom.id ? { ...custom.item, id: custom.id } : custom.item) as T;
    if (custom.position === -1) {
      next.push(entry);
    } else if (custom.position >= 0 && custom.position <= next.length) {
      next.splice(custom.position, 0, entry);
    }
  }
  return next;
}

/** Source items plus override-local custom items that have stable ids. */
function combineCollectionItems<T extends ItemRecord>(
  sourceItems: T[],
  customItems?: CollectionCustomItem[] | null,
): { items: T[]; positionCustoms: CollectionCustomItem[] } {
  if (!customItems?.length) {
    return { items: [...sourceItems], positionCustoms: [] };
  }

  const positionCustoms: CollectionCustomItem[] = [];
  const items = [...sourceItems];
  const seenIds = new Set(
    sourceItems.map(collectionItemId).filter((id): id is string => Boolean(id)),
  );

  for (const custom of customItems) {
    const id = collectionCustomItemId(custom);
    if (!id) {
      positionCustoms.push(custom);
      continue;
    }
    if (seenIds.has(id)) continue;
    seenIds.add(id);
    const entry: ItemRecord = { ...custom.item, id };
    items.push(entry as T);
  }

  return { items, positionCustoms };
}

function applyLegacyOverride<T extends ItemRecord>(
  sourceItems: T[],
  override: CollectionBindingOverride,
  defaultLimit?: number,
): T[] {
  let items = [...sourceItems];

  if (override.hiddenIndices?.length) {
    const hidden = new Set(override.hiddenIndices);
    items = items.filter((_, i) => !hidden.has(i));
  }

  if (override.ordering?.length) {
    // Same contract as orderedIds: listed indices first, then unlisted
    // (newly synced) items append in source order. Hidden indices stay out.
    const original = [...sourceItems];
    const hidden = new Set(override.hiddenIndices || []);
    const seen = new Set<number>();
    const ordered: T[] = [];
    for (const i of override.ordering) {
      if (!Number.isInteger(i) || i < 0 || i >= original.length || hidden.has(i) || seen.has(i)) continue;
      seen.add(i);
      ordered.push(original[i]);
    }
    for (let i = 0; i < original.length; i++) {
      if (hidden.has(i) || seen.has(i)) continue;
      ordered.push(original[i]);
    }
    items = ordered;
  }

  if (override.customItems?.length) {
    items = injectCustomItemsByPosition(items, override.customItems);
  }

  return applyLimit(items, override.limit ?? defaultLimit);
}

export function applyCollectionBindingOverride<T extends ItemRecord>(
  sourceItems: T[],
  override: CollectionBindingOverride | null | undefined,
  options?: { defaultLimit?: number },
): T[] {
  if (!override) {
    return applyLimit([...sourceItems], options?.defaultLimit);
  }

  // Row-level mode: legacy index fields only apply when no id field exists.
  if (override.hiddenIds == null && override.orderedIds == null) {
    return applyLegacyOverride(sourceItems, override, options?.defaultLimit);
  }

  const { items: combined, positionCustoms } = combineCollectionItems(
    sourceItems,
    override.customItems,
  );
  let items = combined;

  if (override.hiddenIds?.length) {
    const hidden = new Set(override.hiddenIds);
    items = items.filter(item => {
      const id = collectionItemId(item);
      return !id || !hidden.has(id);
    });
  }

  if (override.orderedIds != null) {
    items = orderCollectionItemsByIds(items, override.orderedIds, collectionItemId);
  }

  if (positionCustoms.length) {
    items = injectCustomItemsByPosition(items, positionCustoms);
  }

  return applyLimit(items, override.limit ?? options?.defaultLimit);
}
